import React, { useContext, useMemo } from 'react';

import BaseViewsContext from 'contexts/BaseViewsContext';
import BoardsContext from 'contexts/BoardsContext';
import DashboardGadgetsContext from 'contexts/DashboardGadgetsContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import ReportsContext from 'contexts/ReportsContext';
import InsightsViewScopeContext from 'contextProviders/InsightsContentProvider/InsightsViewScopeContext';
import { DefaultDatasetFiltersContext } from 'contextProviders/SplashScreenProviders/DatasetProviders/DefaultDatasetFiltersProvider';
import useGetMetricIdsFromVis from 'hooks/useGetMetricIdsFromVis';
import useRegisterInsightsView from 'hooks/useRegisterInsightsView';

import buildDashboardInsightsView from './buildDashboardInsightsView';

// Publishes the dashboard's live on-screen structure to the AI Insights Chat, and
// scopes the gadgets it wraps so their resolved date/filter scope and rendered
// figures register against this dashboard.
//
// The report analogue (RegisterReportInsightsView) can build the whole view here
// because a report has one shared scope. A dashboard doesn't — each gadget carries
// its own date range and filters, resolved deep in the tree — so this component
// builds only the STRUCTURAL view (which gadgets, their metrics/dimensions) and
// provides InsightsViewScopeContext with the dashboard id; the per-gadget scope
// and figures are captured deeper (see useRegisterDashboardGadgetScope /
// useRegisterInsightsGadgetValues) and stitched on at send time.
const RegisterDashboardInsightsView = ({
  dashboardId,
  cards,
  isDisabled,
  children,
}: {
  dashboardId: string;
  cards: CanvasCard.Card[];
  // Wallboards are passive multi-slide displays, not the user's in-focus content;
  // the entity itself isn't registered for them either.
  isDisabled: boolean;
  children: JSX.Element;
}) => {
  const { dashboardGadgetsLookup } = useContext(DashboardGadgetsContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const { defaultDatasetFiltersLookup } = useContext(
    DefaultDatasetFiltersContext,
  );
  const getMetricIds = useGetMetricIdsFromVis();
  // For the gadgets' popup links. BoardsProvider wraps this component in
  // Dashboard/index.tsx, so both lookups resolve here.
  const { allReports } = useContext(ReportsContext);
  const { boards } = useContext(BoardsContext);
  const reportNamesById = useMemo(
    () => Object.fromEntries(allReports.map((r) => [r.id, r.name])),
    [allReports],
  );
  const boardNamesById = useMemo(
    () => Object.fromEntries(boards.map((b) => [b.id, b.name])),
    [boards],
  );

  const view = useMemo(() => {
    if (isDisabled) {
      return null;
    }
    return buildDashboardInsightsView({
      cards,
      dashboardGadgetsLookup,
      baseViews,
      metricOptionsLookup,
      defaultDatasetFiltersLookup,
      getMetricIds,
      reportNamesById,
      boardNamesById,
    });
  }, [
    isDisabled,
    cards,
    dashboardGadgetsLookup,
    baseViews,
    metricOptionsLookup,
    defaultDatasetFiltersLookup,
    getMetricIds,
    reportNamesById,
    boardNamesById,
  ]);

  useRegisterInsightsView({ entityId: dashboardId, view });

  const scope = useMemo(
    () => ({ entityId: isDisabled ? null : dashboardId }),
    [isDisabled, dashboardId],
  );

  return (
    <InsightsViewScopeContext.Provider value={scope}>
      {children}
    </InsightsViewScopeContext.Provider>
  );
};

export default RegisterDashboardInsightsView;
